import type { ConsentProfile, ConsentRule } from './engine';

// ---------------------------------------------------------------------------
// Internal rule constructors — use the business-intent DSL below, not these.
// ---------------------------------------------------------------------------

// Always rule is a rule that always requires consent.
const always = (
  baseline: ConsentProfile,
  profiles: Partial<Record<string, ConsentProfile>> = {}
): ConsentRule => ({ when: 'always', exemptCountries: [], profiles, baseline });

// Exempt countries are countries whose users are exempt from consent.
const except = (
  exemptCountries: string[],
  baseline: ConsentProfile
): ConsentRule => ({ when: 'except', exemptCountries, profiles: {}, baseline });

const never = (): ConsentRule =>
  ({ when: 'never', exemptCountries: [], profiles: {}, baseline: 'standard' });

// ---------------------------------------------------------------------------
// Business-intent DSL
// ---------------------------------------------------------------------------

/**
 * TCPA consent required for all users.
 * Use for US-focused sites that contact users via automated means.
 *
 * @param exempt - Country codes whose users are exempt from consent.
 */
export const tcpaConsent = (options: { exempt?: string[] } = {}): ConsentRule =>
  options.exempt?.length
    ? except(options.exempt, 'tcpa')
    : always('tcpa');

/**
 * TCPA consent with the eXp In-Service marketing text variant.
 */
export const tcpaInServiceConsent = (): ConsentRule =>
  always('tcpa-in-service');

/**
 * TCPA consent with the Zoocasa-branded marketing text variant.
 * Use for Zoocasa US — shows `marketing_zoocasa_outside_canada` to non-CA users.
 *
 * @param exempt - Country codes whose users are exempt from consent.
 */
export const tcpaZoocasaConsent = (options: { exempt?: string[] } = {}): ConsentRule =>
  options.exempt?.length
    ? except(options.exempt, 'tcpa-zoocasa')
    : always('tcpa-zoocasa');

/**
 * No consent required.
 * Use for sites where users are never contacted via automated means.
 */
export const noConsent = (): ConsentRule => never();

export const NullConsentRule: ConsentRule = noConsent();
